import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import '../styles/Products.css';
import { useCart } from '../context/CartContext';
import { formatBRL } from '../utils/currency';
import '../styles/Cart.css';
import { productLines } from '../data/productLines';
import type { ProductLine } from '../types/catalog';
import ImageModal from './ImageModal';

// Data source moved to src/data/productLines.ts to prepare for backend later

const DEFAULT_BASE_BY_LINE: Record<string, number> = {
  'zero-metal': 8990,
  'resistence-europa': 7990,
  intensive: 9990,
  color: 6990,
  nanoplastia: 11990,
  botox: 12990,
  healing: 6990,
};

const estimatePrice = (line: ProductLine, variantIndex: number) => {
  const product = line.products[variantIndex];
  if (product?.priceCents !== undefined) return product.priceCents;
  const base = DEFAULT_BASE_BY_LINE[line.id] ?? 7990;
  return base + variantIndex * 1000; // preço fictício incremental
};

const Products = () => {
  const [activeProduct, setActiveProduct] = useState<string | null>(null);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { addItem } = useCart();

  const toggleProduct = (id: string) => {
    setActiveProduct(activeProduct === id ? null : id);
  };

  return (
    <section id="products" className="products-section section-spacing">
      <div className="container-custom">
        <div ref={ref} className={`products-header ${inView ? 'visible' : ''}`}>
          <h2 className="section-title">Nossas Linhas</h2>
          <div className="divider" style={{ margin: '40px auto' }}></div>
          <p className="section-description">
            Cada linha foi formulada para atender necessidades específicas,
            desde hidratação intensa até reparação profunda.
          </p>
        </div>

        <div className="products-grid">
          {productLines.map((line, index) => (
            <ProductCard
              key={line.id}
              line={line}
              isActive={activeProduct === line.id}
              onClick={() => toggleProduct(line.id)}
              index={index}
              onAddToCart={(payload) => addItem(payload)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

interface ProductCardProps {
  line: ProductLine;
  isActive: boolean;
  onClick: () => void;
  index: number;
  onAddToCart: (payload: {
    skuId: string;
    productName: string;
    variantName?: string;
    unitPriceCents: number;
    image?: string;
    slug?: string;
    qty?: number;
    isKit?: boolean;
    includes?: string[];
  }) => void;
}

const ProductCard = ({ line, isActive, onClick, index, onAddToCart }: ProductCardProps) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [disabled, setDisabled] = useState<Record<number, boolean>>({});
  const [desktopIncludesOpen, setDesktopIncludesOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const setQty = (idx: number, qty: number) => {
    setQuantities((q) => ({ ...q, [idx]: Math.max(1, qty) }));
  };

  // Auto-rotate images every 6 seconds; pause on hover
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    if (hovered) return; // paused
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % line.images.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [line.images.length, hovered]);

  useEffect(() => {
    if (window.innerWidth > 768) {
      setDesktopIncludesOpen(true);
    }
  }, []);

  return (
    <div
      ref={ref}
      className={`product-card ${isActive ? 'active' : ''} ${inView ? 'visible' : ''}`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <div className="product-card-image-container" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
        {line.images.map((image, idx) => (
          <img
            key={`${line.id}-${idx}`}
            src={image}
            loading="lazy"
            decoding="async"
            alt={line.name}
            className={`product-card-image ${idx === currentImageIndex ? 'active' : ''}`}
            sizes="(max-width: 768px) 92vw, 480px"
            width="800"
            height="800"
            onClick={() => {
              setCurrentImageIndex(idx);
              setIsImageModalOpen(true);
            }}
            style={{ cursor: 'pointer' }}
            title="Clique para expandir"
          />
        ))}
        <div className="image-indicators">
          {line.images.map((_, idx) => (
            <span
              key={idx}
              className={`indicator ${idx === currentImageIndex ? 'active' : ''}`}
              onClick={() => setCurrentImageIndex(idx)}
            />
          ))}
        </div>
      </div>
      <div className="product-card-header" onClick={onClick}>
        <div className="product-card-title">
          <h3>{line.name}</h3>
          <span className="product-subtitle">{line.subtitle}</span>
        </div>
        <div className={`product-card-icon ${isActive ? 'active' : ''}`}>
          <span></span>
          <span></span>
        </div>
      </div>

      <div className={`product-card-content ${isActive ? 'active' : ''}`}>
        <p className="product-description">{line.description}</p>
        <div className="product-list">
          {line.products.map((product, idx) => {
            const priceCents = estimatePrice(line, idx);
            const isKit = Boolean(product.isKit);
            return (
              <div key={idx} className="product-item">
                <div className="product-item-number">{String(idx + 1).padStart(2, '0')}</div>
                <div className="product-item-content">
                  <div className="product-item-title">
                    <h4>{product.name}</h4>
                    {isKit ? <span className="product-badge">Kit</span> : null}
                  </div>
                  <p>{product.description}</p>
                  <div className="product-item-buy">
                    <div className="price">
                      <span className="price-main">{formatBRL(priceCents)}</span>
                    </div>
                    <div className="qty-controls">
                      <button onClick={() => setQty(idx, Math.max(1, (quantities[idx] || 1) - 1))} aria-label="Diminuir">−</button>
                      <input
                        type="number"
                        min={1}
                        value={quantities[idx] || 1}
                        onChange={(e) => setQty(idx, Math.max(1, parseInt(e.target.value || '1', 10)))}
                      />
                      <button onClick={() => setQty(idx, (quantities[idx] || 1) + 1)} aria-label="Aumentar">+</button>
                    </div>
                    <button
                      className="btn btn-warning text-dark fw-semibold"
                      disabled={!!disabled[idx]}
                      onClick={() => {
                        if (disabled[idx]) return;
                        setDisabled((d) => ({ ...d, [idx]: true }));
                        onAddToCart({
                          skuId: `${line.id}-${idx}`,
                          productName: line.name,
                          variantName: product.name,
                          unitPriceCents: priceCents,
                          image: line.images[0],
                          slug: line.id,
                          qty: quantities[idx] || 1,
                          isKit,
                          includes: product.includes,
                        });
                        setTimeout(() => setDisabled((d) => ({ ...d, [idx]: false })), 700);
                      }}
                    >
                      {disabled[idx] ? (
                        'Adicionado'
                      ) : (
                        <>
                          <i className="bi bi-bag-plus me-2" aria-hidden="true"></i>
                          Adicionar
                        </>
                      )}
                    </button>
                  </div>
                  {product.includes?.length ? (
                    <details
                      className="product-includes"
                      aria-label={`Itens incluídos em ${product.name}`}
                      open={desktopIncludesOpen}
                    >
                      <summary>Inclui</summary>
                      <ul>
                        {product.includes.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Image Modal */}
      <ImageModal
        isOpen={isImageModalOpen}
        images={line.images}
        initialIndex={currentImageIndex}
        onClose={() => setIsImageModalOpen(false)}
        productName={line.name}
      />
    </div>
  );
};

export default Products;